import { useCallback, useEffect, useState } from "react";
import type { Role } from "./roles";

const STORAGE_KEY = "nl-team-v2";

export type MemberStatus = "active" | "invited" | "disabled";

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: Role;
  status: MemberStatus;
  last_active: string | null;
  joined_at: string;
  focus?: string;
}

export interface TeamActivity {
  id: string;
  at: string;
  actor: string;
  text: string;
}

function newId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function read(): { members: TeamMember[]; activity: TeamActivity[] } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { members?: TeamMember[]; activity?: TeamActivity[] };
    if (!Array.isArray(parsed.members)) return null;
    return { members: parsed.members, activity: parsed.activity ?? [] };
  } catch {
    return null;
  }
}

function write(members: TeamMember[], activity: TeamActivity[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ members, activity }));
}

export function useTeamStore() {
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [activity, setActivity] = useState<TeamActivity[]>([]);

  useEffect(() => {
    const stored = read();
    setMembers(stored?.members ?? []);
    setActivity(stored?.activity ?? []);
  }, []);

  const persist = useCallback((nextMembers: TeamMember[], nextActivity: TeamActivity[]) => {
    setMembers(nextMembers);
    setActivity(nextActivity);
    write(nextMembers, nextActivity);
  }, []);

  const invite = useCallback((input: {
    name: string;
    email: string;
    role: Role;
    focus?: string;
    actor?: string;
  }) => {
    if (!members) return;
    const next: TeamMember = {
      id: newId(),
      name: input.name.trim() || input.email.split("@")[0],
      email: input.email.trim(),
      role: input.role,
      status: "invited",
      last_active: null,
      joined_at: new Date().toISOString(),
      focus: input.focus?.trim() || undefined,
    };
    persist([next, ...members], [{
      id: newId(),
      at: new Date().toISOString(),
      actor: input.actor ?? "You",
      text: `Invited ${next.name} as ${next.role}`,
    }, ...activity].slice(0, 20));
    return next;
  }, [members, activity, persist]);

  const setRole = useCallback((id: string, role: Role) => {
    if (!members) return;
    const person = members.find((m) => m.id === id);
    persist(
      members.map((m) => (m.id === id ? { ...m, role } : m)),
      [{ id: newId(), at: new Date().toISOString(), actor: "You", text: `Changed ${person?.name ?? "member"} to ${role}` }, ...activity].slice(0, 20),
    );
  }, [members, activity, persist]);

  const setStatus = useCallback((id: string, status: MemberStatus) => {
    if (!members) return;
    persist(members.map((m) => (m.id === id ? { ...m, status, last_active: status === "active" ? new Date().toISOString() : m.last_active } : m)), activity);
  }, [members, activity, persist]);

  const remove = useCallback((id: string, actor?: string) => {
    if (!members) return;
    const person = members.find((m) => m.id === id);
    persist(
      members.filter((m) => m.id !== id),
      [{
        id: newId(),
        at: new Date().toISOString(),
        actor: actor ?? "You",
        text: `Removed ${person?.name ?? "member"}`,
      }, ...activity].slice(0, 20),
    );
  }, [members, activity, persist]);

  return { members: members ?? [], ready: members !== null, activity, invite, setRole, setStatus, remove };
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}
